import { useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import {
  SOCIAL_ACTIONS,
  type LifeView,
  type MyVisitView,
  type PersonAction,
  type VisitHomeReply,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { lookToCharacter, toPreviewCharacter } from '@london-life/game/appearance';
import type { RoomThing } from '@london-life/game/house/room';
import { HouseView } from '../house/house-view';
import { PersonPicture } from '../people/person-card';
import { api } from '../lib/api';
import { notify, notifyProblem } from '../lib/alerts';

const nameOf = (person: { username: string | null; firstName: string }) =>
  person.username ? `@${person.username}` : person.firstName;
// what you can do with the host while you are there (social actions the server checks: you are both in the room)
const TOGETHER = ['say_hello', 'gist', 'crack_joke'] as const;
const secondsUntil = (at: string | null) =>
  at ? Math.max(0, Math.round((new Date(at).getTime() - Date.now()) / 1000)) : 0;

// A visit, as in Lagos Life. At the door: "Knock knock…" until they answer (or the wait runs out), and you can
// walk away. Inside: their home in 3D with them and any other guests in it, a bar with whose place it is and
// whether they are home, things to do together, their chat, and Leave.
export function VisitScreen({
  visit,
  inside,
  life,
  myTag,
  dataSaver,
  busy,
  onLeave,
  onChat,
  onTogether,
  revision,
}: {
  visit: MyVisitView;
  // the door or inside (from where the server says the player is)
  inside: boolean;
  life: LifeView;
  myTag: string;
  dataSaver?: boolean;
  busy: boolean;
  onLeave: () => void;
  onChat: (lifeId: string) => void;
  onTogether: (lifeId: string, body: PersonAction) => Promise<unknown>;
  // changes when the visit changes (someone came or went): the home is read again
  revision: string;
}) {
  const host = visit.host;
  const hostName = nameOf(host);
  const [home, setHome] = useState<VisitHomeReply | null>(null);
  const [left, setLeft] = useState(() => secondsUntil(visit.answerBy));
  useEffect(() => {
    if (inside) return;
    const timer = window.setInterval(() => setLeft(secondsUntil(visit.answerBy)), 1000);
    return () => window.clearInterval(timer);
  }, [inside, visit.answerBy]);
  useEffect(() => {
    if (!inside) return setHome(null);
    let live = true;
    api.visits
      .home()
      .then((reply) => live && setHome(reply))
      .catch((error: unknown) => live && notifyProblem(describeError(error).message));
    return () => {
      live = false;
    };
  }, [inside, revision]);
  // the first look inside: how it works, once per visit
  useEffect(() => {
    if (inside) notify('Come in! Tap the floor to walk around 🛋️', { id: `visit-in:${visit.id}`, duration: 5000 });
  }, [inside, visit.id]);
  const placed: RoomThing[] = useMemo(
    () =>
      (home?.things ?? []).flatMap((thing) =>
        thing.placed ? [{ id: thing.id, item: thing.item, ...thing.placed }] : [],
      ),
    [home],
  );
  const people = useMemo(
    () =>
      home
        ? [
            ...(home.host.atHome
              ? [
                  {
                    id: home.host.lifeId,
                    character: lookToCharacter(home.host.look, home.host.firstName),
                    tag: nameOf(home.host),
                  },
                ]
              : []),
            ...home.guests.map((guest) => ({
              id: guest.lifeId,
              character: lookToCharacter(guest.look, guest.firstName),
              tag: nameOf(guest),
            })),
          ]
        : [],
    [home],
  );
  if (!inside)
    return (
      <div className="visit-door" role="dialog" aria-modal="true" aria-label={`At ${hostName}’s door`}>
        <div className="visit-door-card">
          <div className="visit-door-icon" aria-hidden="true">
            🚪
          </div>
          <h2>Knock knock…</h2>
          <p>
            Waiting for {hostName} to open the door.
            {left > 0 && <small> {left}s</small>}
          </p>
          <button type="button" className="house-button" onClick={onLeave} disabled={busy}>
            Walk away
          </button>
        </div>
      </div>
    );
  return (
    <div
      className="venue-screen house-screen visit-screen"
      role="dialog"
      aria-modal="true"
      aria-label={`${hostName}’s place`}
    >
      {home && (
        <HouseView
          room={home.host.home.room}
          placed={placed}
          character={toPreviewCharacter(life.appearance, life.firstName)}
          dataSaver={dataSaver}
          people={people}
          myTag={myTag}
          lift={0.3}
        />
      )}
      {!home && (
        <p className="house-hint" role="status">
          Coming in…
        </p>
      )}
      <section className="venue-bar" aria-label={`${hostName}’s place`}>
        <header className="venue-head">
          <PersonPicture person={host} />
          <div className="venue-where">
            <h2>
              🏠 {hostName}&rsquo;s place <small>· {host.home.label}</small>
            </h2>
            <p>
              {host.home.area}
              {home?.host.atHome ? " · they're home 🟢" : " · they're out"}
              {home?.guests.length ? ` · ${home.guests.length + 1} guests` : ''}
            </p>
          </div>
          <div className="venue-actions">
            <button type="button" aria-label={`Chat with ${hostName}`} onClick={() => onChat(host.lifeId)}>
              <MessageCircle size={17} />
            </button>
          </div>
        </header>
        <div className="venue-cards" role="group" aria-label={`With ${hostName}`}>
          {TOGETHER.map((kind) => {
            const rule = SOCIAL_ACTIONS[kind];
            return (
              <button
                key={kind}
                type="button"
                className="venue-card"
                disabled={busy}
                onClick={() => void onTogether(host.lifeId, { action: 'social', kind })}
              >
                <span className="venue-card-top">
                  <span className="venue-card-icon" aria-hidden="true">
                    {rule.icon}
                  </span>
                  <span className="venue-card-meta">
                    <small>⏱ {rule.seconds}s</small>
                  </span>
                </span>
                <strong>{rule.label}</strong>
              </button>
            );
          })}
        </div>
        <button type="button" className="house-button visit-leave" onClick={onLeave} disabled={busy}>
          🚶 Leave
        </button>
      </section>
    </div>
  );
}
